import { useState, useEffect } from 'react'
import { TaxonomyIntent } from '@/hooks/useTaxonomyRun'
import { useApproval } from '@/hooks/useApproval'
import { ApprovalBar } from './ApprovalBar'
import { IntentCard }  from './IntentCard'

interface TaxonomyReviewViewProps {
  intents: TaxonomyIntent[]
  kbId:    string
  runId:   string
}

export function TaxonomyReviewView({ intents: rawIntents, kbId, runId: _runId }: TaxonomyReviewViewProps) {
  // ── Local copy of intents — this is what the UI reads/writes ──
  const [intents, setIntents] = useState<TaxonomyIntent[]>(rawIntents)

  // Sync when the parent re-fetches (e.g. after page refresh)
  useEffect(() => {
    setIntents(rawIntents)
  }, [rawIntents])

  // ── Approval actions from hook ──
  const {
    approveIntent,
    rejectIntent,
    editIntent,
    approveTag,
    rejectTag,
    approveEntity,
    rejectEntity,
  } = useApproval({ kbId, intents, setIntents })

  // ── Bulk actions ──
  const approveAll = () => {
    // Only approve intents that are still pending
    intents.filter(i => i.approved === 'pending').forEach(i => approveIntent(i.id))
  }

  const resetAll = () => {
    // Reset local state only — sets all intents back to pending.
    // No PATCH call. Task 7 tightens this with proper state machine enforcement.
    setIntents(prev => prev.map(i => ({
      ...i,
      approved: 'pending',
      tags:         i.tags.map(t => ({ ...t, approved: 'pending' })),
      entity_types: i.entity_types.map(e => ({ ...e, approved: 'pending' })),
    })))
  }

  // ── Empty state: all intents rejected ──
  const allRejected = intents.length > 0 && intents.every(i => i.approved === 'rejected')

  if (allRejected) {
    return (
      <div className="py-12 text-center text-gray-500 text-sm">
        No active intents. Re-run the pipeline to rediscover.
      </div>
    )
  }

  // ── Render ──
  return (
    <div className="mt-2">
      {/* Approval summary bar + bulk actions */}
      <ApprovalBar intents={intents} onApproveAll={approveAll} onResetAll={resetAll} />

      {/* Intent cards */}
      <div className="space-y-2">
        {intents.map(intent => (
          <IntentCard
            key={intent.id}
            intent={intent}
            onApprove={approveIntent}
            onReject={rejectIntent}
            onEdit={editIntent}
            onApproveTag={approveTag}
            onRejectTag={rejectTag}
            onApproveEntity={approveEntity}
            onRejectEntity={rejectEntity}
          />
        ))}
      </div>
    </div>
  )
}
